/** Policies Directus du module Dispatching (créées avec ces identifiants par `provision-dispatch-directus.mjs`). */
export const DISPATCH_MEMBER_POLICY_ID = '10292509-8485-46c1-99cd-f55bb03d7eb8'
export const DISPATCH_MANAGER_POLICY_ID = 'd9e01caf-8b4f-4157-955f-332fca24aa9c'
export const DISPATCH_ON_CALL_POLICY_ID = '68a8bbba-374c-401b-9815-07d22636b8a7'

/** Tous les horaires du module sont exprimés à l'heure de Bruxelles (changements d'heure compris). */
export const DISPATCH_TIMEZONE = 'Europe/Brussels'

/** Quatre shifts de six heures par jour, un seul dispatcheur à la fois. */
export const SHIFT_SLOTS = ['night', 'morning', 'afternoon', 'evening'] as const
export type ShiftSlot = typeof SHIFT_SLOTS[number]

/** Heures locales de début et de fin (24 = minuit du lendemain). */
export const SLOT_HOURS: Record<ShiftSlot, { start: number, end: number, label: string }> = {
  night: { start: 0, end: 6, label: 'Nuit' },
  morning: { start: 6, end: 12, label: 'Matin' },
  afternoon: { start: 12, end: 18, label: 'Après-midi' },
  evening: { start: 18, end: 24, label: 'Soir' },
}

/** Permanence des cadres : deux plages de 12 heures, comme les gardes eBrigade « Cadre de Permanence ». */
export const ON_CALL_PLAGES = ['day', 'night'] as const
export type OnCallPlage = typeof ON_CALL_PLAGES[number]
export const ON_CALL_HOURS: Record<OnCallPlage, { start: number, end: number, label: string }> = {
  day: { start: 7, end: 19, label: 'Journée' },
  night: { start: 19, end: 31, label: 'Nuit' },
}

/** Mode de couverture d'une semaine (lundi au dimanche). Sans choix explicite, une semaine est en mode réduit. */
export const COVERAGE_MODES = ['full', 'reduced', 'closed'] as const
export type CoverageMode = typeof COVERAGE_MODES[number]
export const COVERAGE_MODE_LABELS: Record<CoverageMode, string> = { full: 'Complète', reduced: 'Réduite', closed: 'Fermée' }

/** Statut final d'un créneau après application du gabarit, du mode de la semaine et des exceptions. */
export const SLOT_COVERAGE_STATUSES = ['closed', 'standard', 'featured'] as const
export type SlotCoverageStatus = typeof SLOT_COVERAGE_STATUSES[number]

export const MONTH_STATUSES = ['draft', 'open', 'arbitration', 'published', 'closed'] as const
export type MonthStatus = typeof MONTH_STATUSES[number]
export const MONTH_STATUS_LABELS: Record<MonthStatus, string> = {
  draft: 'Brouillon',
  open: 'Désidératas ouverts',
  arbitration: 'Arbitrage',
  published: 'Publié',
  closed: 'Clos',
}

export const WISH_STATUSES = ['pending', 'accepted', 'rejected', 'withdrawn'] as const
export type WishStatus = typeof WISH_STATUSES[number]

export const SHIFT_STATUSES = ['free', 'validated', 'done', 'absent', 'cancelled'] as const
export type ShiftStatus = typeof SHIFT_STATUSES[number]
export const SHIFT_STATUS_LABELS: Record<ShiftStatus, string> = {
  free: 'Libre',
  validated: 'Validé',
  done: 'Effectué',
  absent: 'Absent',
  cancelled: 'Annulé',
}

/** Réglages par défaut (modifiables par les administrateurs dans `dispatch_settings`). */
export const DISPATCH_DEFAULTS = {
  quotaMin: 4,
  quotaMax: 16,
  /** Coefficient de points par type de shift (entiers, 0 autorisé). */
  coefficients: { night: 1, morning: 1, afternoon: 1, evening: 1 } as Record<ShiftSlot, number>,
  /** Retrait d'un shift validé : en deçà, seul le coordinateur peut l'autoriser. */
  withdrawalLockHours: 48,
  /** Relève : le point revient au titulaire s'il a tenu au moins ce temps, sinon au remplaçant. */
  handoverThresholdHours: 3,
  /** Repos : avertissement au-delà de ce nombre d'heures consécutives. */
  restWarningHours: 12,
  /** Absence à court préavis. */
  shortNoticeHours: 12,
  shortNoticeRepeatMinutes: 30,
  /** Seuil de tension : taux de couverture en deçà duquel les dispatcheurs sous quota sont relancés. */
  tensionThreshold: 0.85,
  tensionLeadDays: 10,
  /** Pondérations de l'équité (sans lien avec les points). */
  effortWeights: { night: 0.5, weekend: 0.5 },
  /** Prise de poste : ouverte 30 minutes avant, jusqu'à 1 heure après le début ; alerte à H+15 minutes. */
  checkInOpensMinutesBefore: 30,
  checkInClosesMinutesAfter: 60,
  checkInMissingMinutesAfter: 15,
} as const

/** Gardes eBrigade (code GAR) remplacées par le module à la date de bascule : elles ne doivent plus être comptées par eBrigade. */
export const EBRIGADE_DISPATCH_LABELS = ['Dispatching', 'Cadre de Permanence'] as const
export const EBRIGADE_GUARD_CODE = 'GAR'

/** Touches du standard OVH pilotées par le module. */
export const PHONE_KEYS = ['1', '99'] as const
export type PhoneKey = typeof PHONE_KEYS[number]
export const PHONE_KEY_LABELS: Record<PhoneKey, string> = { 1: 'Dispatch', 99: 'Cadre de permanence' }
